import chalk from 'chalk'
import inquirer, { Question } from 'inquirer'
import emoji from 'node-emoji'
import path from 'path'
import R from 'ramda'
import { Argv } from 'yargs'

import { generateTemplate, writeTemplateFileSync } from '../../utils'
import { isValidBasicStringOfLengthN } from '../validators'

interface ExtendedQuestion extends Question {
  choices?: string[]
}

const askQuestions = inquirer.prompt

const platformNotSupplied = R.propEq('platform', false)
const platformIsiOS = R.propEq('platform', 'ios')

const iOSTemplatePath = path.resolve(__dirname, '..', '..', '..', 'templates', 'fastlane', 'ios', 'Fastfile')

const androidTemplatePath = path.resolve(__dirname, '..', '..', '..', 'templates', 'fastlane', 'android', 'Fastfile')

const iOSOutputDirectory = path.resolve('ios', 'fastlane')
const androidOutputDirectory = path.resolve('android', 'fastlane')

const iOSQuestions: ExtendedQuestion[] = [
  {
    type: 'input',
    name: 'appIdentifier',
    prefix: emoji.get('rocket'),
    message: `Please enter your iOS application bundle name ${chalk.underline.green('(e.g. io.yourappname)')}:`,

    validate: isValidBasicStringOfLengthN(2),
  },
  {
    type: 'input',
    name: 'projectName',
    prefix: emoji.get('i_love_you_hand_sign'),
    message: `The name of your iOS project ${chalk.underline.green('(used to set the scheme for the gym command)')}:`,
    validate: isValidBasicStringOfLengthN(2),
  },
  {
    type: 'input',
    name: 'testFlightBuildConfiguration',
    prefix: emoji.get('smiley_cat'),
    default: 'Release',
    message: `The build configuration ${chalk.underline.green('(default Release)')} used when building for Testflight:`,
  },
]

const androidQuestions: ExtendedQuestion[] = [
  {
    type: 'input',
    name: 'appName',
    prefix: emoji.get('rocket'),
    message: `Please enter your android application name ${chalk.underline.green('(e.g. Example App)')}:`,

    validate: isValidBasicStringOfLengthN(2),
  },
  {
    type: 'list',
    name: 'betaTrack',
    prefix: emoji.get('i_love_you_hand_sign'),
    choices: ['internal', 'alpha', 'beta'],
    message: 'Please choose a track to use for beta deploys',
  },
]

const generateiOS = async (questions: ExtendedQuestion[]) => {
  try {
    console.info(
      `${chalk.red('Starting generation of fastlane template for iOS... this will overwrite your Fastfile')} \n`,
    )
    const answers = await askQuestions(questions)
    const templateString = generateTemplate(iOSTemplatePath, answers)
    writeTemplateFileSync(iOSOutputDirectory, 'Fastfile', templateString)
    console.info('Generated Fastfile at:', iOSOutputDirectory)
  } catch (error) {
    console.error(error.message)
  }
}

const generateAndroid = async (questions: ExtendedQuestion[]) => {
  try {
    console.info(
      `${chalk.red('Starting generation of fastlane template for android... this will overwrite your Fastfile')} \n`,
    )
    const answers = await askQuestions(questions)
    const templateString = generateTemplate(androidTemplatePath, answers)
    writeTemplateFileSync(androidOutputDirectory, 'Fastfile', templateString)
    console.info('Generated Fastfile at:', androidOutputDirectory)
  } catch (error) {
    console.error(error.message)
  }
}

const generateAll = () => {
  console.error('No platform provided.')
}

const generateForPlatform = R.ifElse(
  platformIsiOS,
  async () => generateiOS(iOSQuestions),
  async () => generateAndroid(androidQuestions),
)

export const command = 'generate [platform]'
export const desc = 'Generate fastlane boilerplate in your React Native project for platform [platform]'
export const builder = (yargs: Argv): Argv => yargs.default('platform', false)
export const handler = R.ifElse(platformNotSupplied, generateAll, generateForPlatform)
